import { useParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { useUploadDetail } from "@/hooks/useUploads"
import { ArrowLeftIcon } from "@heroicons/react/24/outline"

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    in_progress: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-600",
    active: "bg-blue-100 text-blue-800",
    ended: "bg-gray-100 text-gray-600",
    superseded: "bg-yellow-100 text-yellow-800",
    invalid: "bg-red-100 text-red-800",
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status] || "bg-gray-100 text-gray-600"}`}>
      {status.replace("_", " ")}
    </span>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm text-secondary">{label}</dt>
      <dd className="text-sm font-medium text-primary mt-0.5">{value}</dd>
    </div>
  )
}

export function UploadDetail() {
  const { batchId } = useParams<{ batchId: string }>()
  const { data, isLoading, error } = useUploadDetail(batchId || "")

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-40 bg-gray-200 rounded" />
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <a href="/uploads" className="text-secondary hover:text-primary">
            <ArrowLeftIcon className="h-5 w-5" />
          </a>
          <div>
            <h1 className="text-2xl font-bold text-primary font-mono">{data.public_id}</h1>
            <p className="text-secondary">Upload batch detail</p>
          </div>
        </div>

        {/* Batch info */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-primary mb-4">Batch Info</h2>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Status" value={<StatusBadge status={data.status} />} />
            <Field label="Phase" value={data.phase} />
            <Field label="Progress" value={`${data.completed_items}/${data.total_items} (${data.failed_items} failed)`} />
            <Field label="Revision" value={data.latest_revision} />
            <Field label="Game" value={<span className="font-mono">{data.game_public_id || "—"}</span>} />
            <Field label="User" value={`${data.user.name || "—"} (${data.user.email})`} />
            <Field label="Started" value={new Date(data.created_at).toLocaleString()} />
            <Field label="Updated" value={new Date(data.updated_at).toLocaleString()} />
          </dl>
        </div>

        {/* Sessions */}
        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold text-primary mb-4">
            Live Activity Sessions ({data.sessions.length})
          </h2>
          {data.sessions.length === 0 ? (
            <p className="text-sm text-secondary">No Live Activity sessions registered for this batch.</p>
          ) : (
            <div className="space-y-3">
              {data.sessions.map((session) => (
                <div key={session.public_id} className="border border-border rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm">{session.public_id}</span>
                    <StatusBadge status={session.status} />
                  </div>
                  <dl className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <Field label="Environment" value={session.environment} />
                    <Field label="Bundle ID" value={session.bundle_id} />
                    <Field label="Last Sent Rev" value={session.last_sent_revision} />
                    <Field label="Created" value={new Date(session.created_at).toLocaleString()} />
                  </dl>
                  <div className="mt-2">
                    <span className="text-xs text-secondary">Token: </span>
                    <code className="text-xs font-mono text-secondary break-all">
                      {session.push_token.substring(0, 20)}...
                    </code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
